import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Euro } from 'lucide-react'
import { InvoicePaymentCreateForm } from '@/components/payments/PaymentCreateDrawerContent'
import { formatDate, roundCurrencyAmount } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  PAYMENT_METHOD_LABELS,
  type InvoiceDetail,
  type Payment,
} from '@/types'
import {
  InvoiceTabSectionCard,
  InvoiceTabSectionHeading,
} from './InvoiceTabSection'
import { formatInvoiceAmount } from './invoiceTabStyles'

export function InvoicePaymentsTab({
  invoice,
  payments,
  isLoading,
  isError,
  canEditPayments,
  canViewPayment,
  onOpenPayment,
}: {
  invoice: InvoiceDetail
  payments: Payment[]
  isLoading: boolean
  isError: boolean
  canEditPayments: boolean
  canViewPayment: (paymentId: string) => boolean
  onOpenPayment: (paymentId: string) => void
}) {
  const { t } = useTranslation('invoices')
  const queryClient = useQueryClient()
  const balanceDue = roundCurrencyAmount(
    (invoice.total || 0) - (invoice.amount_paid || 0)
  )

  return (
    <InvoiceTabSectionCard>
      <InvoiceTabSectionHeading
        title={t('detail.paymentReceived')}
        icon={<Euro className='size-4' strokeWidth={2.25} />}
      />
      <div className='px-6 pb-5 pt-4'>
        {isLoading ? (
          <p className='py-6 text-center text-sm font-normal text-[#5a5a5a]'>
            {t('detail.loading')}
          </p>
        ) : isError ? (
          <p className='py-6 text-center text-sm font-normal text-destructive'>
            {t('detail.paymentsLoadError')}
          </p>
        ) : payments.length === 0 ? (
          <p className='py-6 text-center text-sm font-normal text-[#5a5a5a]'>
            {t('detail.noPayments')}
          </p>
        ) : (
          <div className='flex flex-col gap-2'>
            {payments.map(p => {
              const canOpenPayment = canViewPayment(p.id)
              const paymentMeta = (() => {
                switch (p.status) {
                  case 'pending':
                    return t('detail.paymentNotYetReceived')
                  case 'failed':
                    return t('detail.paymentFailedOn', {
                      date: formatDate(p.failed_at ?? p.created_at),
                    })
                  case 'refunded':
                    return t('detail.paymentRefundedOn', {
                      date: formatDate(p.refunded_at ?? p.created_at),
                    })
                  default:
                    return t('detail.paymentReceivedOn', {
                      date: formatDate(p.paid_date ?? p.created_at),
                    })
                }
              })()

              return (
                <button
                  key={p.id}
                  type='button'
                  disabled={!canOpenPayment}
                  className={cn(
                    'flex w-full items-center gap-3 overflow-hidden rounded-xl border border-[#e7e9ef] bg-[#f4f5f8] p-3 text-left',
                    canOpenPayment &&
                      'cursor-pointer transition-colors hover:bg-[#eef0f5]'
                  )}
                  onClick={() => onOpenPayment(p.id)}
                >
                  <div className='flex min-w-0 flex-1 items-center gap-2'>
                    <span className='shrink-0 text-sm font-medium leading-[1.4] text-[#171a20]'>
                      {PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                    </span>
                    <span className='h-1 w-1 shrink-0 rounded-full bg-[#89909c]' />
                    <span className='truncate text-sm font-normal leading-[1.4] text-[#3d434e]'>
                      {paymentMeta}
                    </span>
                  </div>
                  <span className='shrink-0 whitespace-nowrap text-sm font-medium leading-[1.4] text-[#171a20]'>
                    {formatInvoiceAmount(p.amount)}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </div>
      {canEditPayments && (
        <div className='border-t border-border px-6 pb-6 pt-4'>
          <InvoicePaymentCreateForm
            key={`invoice-payment:${invoice.id}:${balanceDue.toFixed(2)}`}
            initialClientId={invoice.client_id}
            initialInvoiceId={invoice.id}
            initialInvoice={invoice}
            onCancel={() => undefined}
            onRecorded={() => {
              queryClient.invalidateQueries({
                queryKey: ['payments', 'list'],
              })
              queryClient.invalidateQueries({
                queryKey: ['invoices', 'detail', invoice.id],
              })
              queryClient.invalidateQueries({
                queryKey: ['invoices', 'list'],
              })
              queryClient.invalidateQueries({
                queryKey: ['invoices', 'summary'],
              })
            }}
          />
        </div>
      )}
    </InvoiceTabSectionCard>
  )
}
